import { ColumnDescriptorProps, ListTable } from '../../../../../components/table/ListTable';
import { LogDeviceWhitelist } from './LogDeviceWhitelistForm';
import Tag from '@rio-cloud/rio-uikit/Tag';
import { useIntl } from 'react-intl';
import { NoData } from '../../../../../components/PropertyTableFieldDefinitions';
import React, { useState } from 'react';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import { LogDeviceWhitelistFormDialog } from './LogDeviceWhitelistFormDialog';
import {
    useDeleteDeviceMutation,
    useGetAllCm4Query,
    useGetAllTbm3Query,
} from '../../../../../services/logDeviceWhitelistCm4Tbm3Api';
import { useDeleteOcu3Mutation, useGetAllOcu3Query } from '../../../../../services/logDeviceWhitelistOcu3Api';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { DEVICE_TYPE } from '../../device-sidebar/device-state.types';

export const LogDeviceWhitelistList = () => {
    const { formatMessage } = useIntl();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [showEditForm, setShowEditForm] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<LogDeviceWhitelist | undefined>();
    const { data: dataCm4, error: errorCm4, isLoading: isLoadingCm4 } = useGetAllCm4Query();
    const { data: dataTbm3, error: errorTbm3, isLoading: isLoadingTbm3 } = useGetAllTbm3Query();
    const { data: dataOcu3, error: errorOcu3, isLoading: isLoadingOcu3 } = useGetAllOcu3Query();
    const [activeTab, setActiveTab] = useState<'CM4' | 'TBM3' | 'OCU3'>('CM4');

    const [deleteLogDeviceWhitelist] = useDeleteDeviceMutation();
    const [deleteLogDeviceWhitelistOcu3] = useDeleteOcu3Mutation();

    const editRow = (item: LogDeviceWhitelist | undefined) => {
        setSelectedItem(item);
        setShowEditForm(true);
    };

    const deleteRow = (item: LogDeviceWhitelist | undefined) => {
        setShowDeleteConfirmation(true);
        setSelectedItem(item);
    };

    const deleteItem = async () => {
        if (!selectedItem) {
            throw Error('No item selected.');
        }
        try {
            if (selectedItem.type === DEVICE_TYPE.CM4 || selectedItem.type.toLowerCase() === 'tbm') {
                await deleteLogDeviceWhitelist(
                    `${selectedItem.type.toLowerCase()}:${selectedItem.serial_number}`
                ).unwrap();
            }
            if (selectedItem.type === DEVICE_TYPE.OCU3) {
                await deleteLogDeviceWhitelistOcu3(`${selectedItem.device_id}`).unwrap();
            }
            Notification.info('Successfully deleted');
        } catch {
            Notification.error('Could not delete');
        } finally {
            setShowDeleteConfirmation(false);
            setSelectedItem(undefined);
        }
    };

    const columnDescriptorsCm4: ColumnDescriptorProps<LogDeviceWhitelist>[] = [
        {
            id: 'serialNumber',
            field: 'serial_number',
            label: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.serialNumber',
            format: (field) => <span>{field}</span>,
        },
        {
            id: 'type',
            field: 'type',
            label: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.deviceType',
            className: 'text-center',
            format: (field) => <div className="label label-primary label-condensed">{field}</div>,
        },
        {
            id: 'cids',
            field: 'cids',
            label: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.contentIds',
            format: (_, item?: LogDeviceWhitelist) => (
                <span>
                    {!item?.cids || item?.cids?.length === 0 ? (
                        <NoData />
                    ) : (
                        item.cids.map((cid, index) => (
                            <Tag className="margin-right-10 margin-bottom-5" key={`cid-${index}`}>
                                {cid}
                            </Tag>
                        ))
                    )}
                </span>
            ),
        },
        {
            id: 'requester',
            field: 'requester',
            label: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.requester',
            format: (field) => <span>{field}</span>,
        },
        {
            id: 'reason',
            field: 'reason',
            label: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.reason',
            format: (field) => <span>{field}</span>,
        },
        {
            id: 'handler',
            field: 'originator',
            format: (_, item?: LogDeviceWhitelist) => (
                <div className="btn-toolbar margin-bottom-20">
                    <div className="btn-group">
                        <button type="button" className="btn btn-default" onClick={() => editRow(item)}>
                            <span className="rioglyph rioglyph-pencil text-size-14 margin-right-0" />
                        </button>
                        <button type="button" className="btn btn-default" onClick={() => deleteRow(item)}>
                            <span className="rioglyph rioglyph-trash text-size-14" />
                        </button>
                    </div>
                </div>
            ),
        },
    ];

    const columnDescriptorsOcu3: ColumnDescriptorProps<LogDeviceWhitelist>[] = [
        {
            id: 'deviceId',
            field: 'device_id',
            label: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.deviceId',
            format: (field) => <span>{field}</span>,
        },
        {
            id: 'type',
            field: 'type',
            label: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.deviceType',
            className: 'text-center',
            format: (field) => <div className="label label-primary label-condensed">{field}</div>,
        },
        {
            id: 'requester',
            field: 'requester',
            label: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.requester',
            format: (field) => <span>{field}</span>,
        },
        {
            id: 'reason',
            field: 'reason',
            label: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.reason',
            format: (field) => <span>{field}</span>,
        },
        {
            id: 'handler',
            field: 'device_id',
            format: (_, item?: LogDeviceWhitelist) => (
                <div className="btn-toolbar margin-bottom-20">
                    <div className="btn-group">
                        <button type="button" className="btn btn-default" onClick={() => editRow(item)}>
                            <span className="rioglyph rioglyph-pencil text-size-14 margin-right-0" />
                        </button>
                        <button type="button" className="btn btn-default" onClick={() => deleteRow(item)}>
                            <span className="rioglyph rioglyph-trash text-size-14" />
                        </button>
                    </div>
                </div>
            ),
        },
    ];

    const addNewDeviceWhitelist = () => {
        setSelectedItem(undefined);
        setShowEditForm(true);
    };

    return (
        <>
            <ul className="nav nav-tabs">
                <li className={activeTab === 'CM4' ? 'active' : ''}>
                    <a
                        href="#tab1"
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('CM4');
                        }}
                    >
                        CM4
                    </a>
                </li>
                <li className={activeTab === 'TBM3' ? 'active' : ''}>
                    <a
                        href="#tab2"
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('TBM3');
                        }}
                    >
                        TBM3
                    </a>
                </li>
                <li className={activeTab === 'OCU3' ? 'active' : ''}>
                    <a
                        href="#tab3"
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('OCU3');
                        }}
                    >
                        OCU3
                    </a>
                </li>
            </ul>
            <div id="whitelistTabs" className="tab-content">
                {activeTab === 'CM4' && (
                    <div className="tab-pane fade active in" id="tab1">
                        <div className="form-group">
                            <div className="display-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-default margin-right-10"
                                    onClick={addNewDeviceWhitelist}
                                >
                                    Add new
                                </button>
                            </div>
                        </div>
                        <ListTable
                            items={isLoadingCm4 ? [] : dataCm4!}
                            isLoading={isLoadingCm4}
                            itemKey="originator"
                            showHeader
                            columnDescriptors={columnDescriptorsCm4}
                        />
                        <ConfirmationDialog
                            show={showDeleteConfirmation}
                            title={formatMessage({
                                id: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.deleteDialog.title',
                            })}
                            content={formatMessage({
                                id: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.deleteDialog.content',
                            })}
                            bsSize={Dialog.SIZE_SM}
                            onClickConfirm={() => deleteItem()}
                            onClickCancel={() => setShowDeleteConfirmation(false)}
                            cancelButtonText={formatMessage({ id: 'intl-msg:coreconAdminWeb.cancel' })}
                            confirmButtonText={
                                <>
                                    <span className="rioglyph rioglyph-ok margin-right-5" />
                                    <span>{formatMessage({ id: 'intl-msg:coreconAdminWeb.confirm' })}</span>
                                </>
                            }
                            useOverflow
                        />
                        <LogDeviceWhitelistFormDialog
                            preSelectedType={DEVICE_TYPE.CM4}
                            show={showEditForm}
                            edit={selectedItem}
                            onClose={() => setShowEditForm(false)}
                        />
                    </div>
                )}
                {activeTab === 'TBM3' && (
                    <div className="tab-pane fade active in" id="tab2">
                        <div className="form-group">
                            <div className="display-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-default margin-right-10"
                                    onClick={addNewDeviceWhitelist}
                                >
                                    Add new
                                </button>
                            </div>
                        </div>
                        <ListTable
                            items={isLoadingCm4 ? [] : dataTbm3!}
                            isLoading={isLoadingCm4}
                            itemKey="originator"
                            showHeader
                            columnDescriptors={columnDescriptorsCm4}
                        />
                        <ConfirmationDialog
                            show={showDeleteConfirmation}
                            title={formatMessage({
                                id: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.deleteDialog.title',
                            })}
                            content={formatMessage({
                                id: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.deleteDialog.content',
                            })}
                            bsSize={Dialog.SIZE_SM}
                            onClickConfirm={() => deleteItem()}
                            onClickCancel={() => setShowDeleteConfirmation(false)}
                            cancelButtonText={formatMessage({ id: 'intl-msg:coreconAdminWeb.cancel' })}
                            confirmButtonText={
                                <>
                                    <span className="rioglyph rioglyph-ok margin-right-5" />
                                    <span>{formatMessage({ id: 'intl-msg:coreconAdminWeb.confirm' })}</span>
                                </>
                            }
                            useOverflow
                        />
                        <LogDeviceWhitelistFormDialog
                            preSelectedType={DEVICE_TYPE.TBM3}
                            show={showEditForm}
                            edit={selectedItem}
                            onClose={() => setShowEditForm(false)}
                        />
                    </div>
                )}
                {activeTab === 'OCU3' && (
                    <div className="tab-pane fade active in" id="tab3">
                        <div className="form-group">
                            <div className="display-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-default margin-right-10"
                                    onClick={addNewDeviceWhitelist}
                                >
                                    Add new
                                </button>
                            </div>
                        </div>
                        <ListTable
                            items={isLoadingOcu3 ? [] : dataOcu3!}
                            isLoading={isLoadingOcu3}
                            itemKey="device_id"
                            showHeader
                            columnDescriptors={columnDescriptorsOcu3}
                        />
                        <ConfirmationDialog
                            show={showDeleteConfirmation}
                            title={formatMessage({
                                id: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.deleteDialog.title',
                            })}
                            content={formatMessage({
                                id: 'intl-msg:coreconAdminWeb.logDeviceWhitelist.deleteDialog.content',
                            })}
                            bsSize={Dialog.SIZE_SM}
                            onClickConfirm={() => deleteItem()}
                            onClickCancel={() => setShowDeleteConfirmation(false)}
                            cancelButtonText={formatMessage({ id: 'intl-msg:coreconAdminWeb.cancel' })}
                            confirmButtonText={
                                <>
                                    <span className="rioglyph rioglyph-ok margin-right-5" />
                                    <span>{formatMessage({ id: 'intl-msg:coreconAdminWeb.confirm' })}</span>
                                </>
                            }
                            useOverflow
                        />
                        <LogDeviceWhitelistFormDialog
                            preSelectedType={DEVICE_TYPE.OCU3}
                            show={showEditForm}
                            edit={selectedItem}
                            onClose={() => setShowEditForm(false)}
                        />
                    </div>
                )}
            </div>
        </>
    );
};